import { useState, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import UserView from './components/UserView'
import AdminDashboard from './components/AdminDashboard'
import TaskDashboard from './components/TaskDashboard'
import AssetDashboard from './components/AssetDashboard'
import LandingPage from './components/LandingPage'
import AppLayout from './components/AppLayout'
import SiteMapView from './components/SiteMapView'
import { User, VersionResponse } from './types'
import { fetchWithAuth, isLoggedIn } from './utils/api'

function App() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [uptime, setUptime] = useState<string>('')
  const [version, setVersion] = useState<VersionResponse | null>(null)

  useEffect(() => {
    // Only fetch user data if logged in - don't redirect here
    if (!isLoggedIn()) {
      setLoading(false)
      return
    }
    fetchUser()
  }, [])

  // Fetch uptime for admin users
  useEffect(() => {
    if (user && user.isAdmin) {
      fetchUptime()
      fetchVersion()
      // Update uptime every 30 seconds
      const interval = setInterval(fetchUptime, 30000)
      return () => clearInterval(interval)
    }
  }, [user])

  const fetchUser = async () => {
    try {
      const response = await fetchWithAuth('/api/user')
      if (!response.ok) {
        throw new Error('Failed to fetch user info')
      }
      const userData = await response.json()
      const normalized: User = {
        ...userData,
        isAdmin: !!userData.isAdmin,
      }
      setUser(normalized)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user info')
    } finally {
      setLoading(false)
    }
  }

  const fetchUptime = async () => {
    try {
      const response = await fetchWithAuth('/api/uptime')
      if (response.ok) {
        const data = await response.json()
        setUptime(data.uptimeFormatted)
      }
    } catch (err) {
      console.error('Failed to fetch uptime:', err)
    }
  }

  const fetchVersion = async () => {
    try {
      const response = await fetchWithAuth('/api/version')
      if (response.ok) {
        const data: VersionResponse = await response.json()
        setVersion(data)
      }
    } catch (err) {
      console.error('Failed to fetch version:', err)
    }
  }

  if (loading) {
    return (
      <div className="container">
        <div className="loading">Loading...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container">
        <div className="error">{error}</div>
      </div>
    )
  }

  // Remove the blocking condition - allow routes to render even without user

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      {user && (
        <>
          <Route path="/app" element={
            <AppLayout user={user} uptime={uptime} version={version}>
              <UserView user={user} />
            </AppLayout>
          } />
          <Route path="/tasks" element={
            <AppLayout user={user} uptime={uptime} version={version}>
              <TaskDashboard user={user} />
            </AppLayout>
          } />
          <Route path="/assets" element={
            <AppLayout user={user} uptime={uptime} version={version}>
              <AssetDashboard user={user} />
            </AppLayout>
          } />
          <Route path="/site-map" element={
            <AppLayout user={user} uptime={uptime} version={version} fullBleed>
              <SiteMapView />
            </AppLayout>
          } />
          {Boolean(user.isAdmin) ? (
            <Route path="/admin/*" element={
              <AppLayout user={user} uptime={uptime} version={version}>
                <AdminDashboard user={user} />
              </AppLayout>
            } />
          ) : null}
        </>
      )}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
